import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { AdminThemeProvider } from "@/contexts/AdminThemeContext";
import { AdminSearchFilter } from "@/components/admin/AdminSearchFilter";
import AdminErrorBoundary, { AdminSectionErrorBoundary } from "@/components/admin/AdminErrorBoundary";
import { 
  AnalyticsDashboardSkeleton, 
  CreditManagementSkeleton, 
  ReferralManagementSkeleton,
  TableSkeleton,
  FormSkeleton,
  ProgressiveLoader
} from "@/components/admin/AdminSkeletons";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { apiRequest } from "@/lib/queryClient";
import { 
  useAdminDashboardSummary,
  useModelUsageByPurpose,
  useModelUsageByPlan,
  useRevenueAnalytics,
  useCouponAnalytics,
  useReferralAnalytics
} from "@/hooks/useAdminAnalytics";
import {
  usePlanCreditConfigs,
  useBulkGrantCredits,
  useBulkDeductCredits,
  useUpdatePlanCreditConfig,
  useUpdateUserPlan,
  useTaskCreditConfigs,
  useUpdateTaskCreditConfig,
  useCreateTaskCreditConfig
} from "@/hooks/useCreditManagement";
import {
  usePlanPricingConfigs,
  useUpdatePlanPricing,
  useCreatePlanPricing
} from "@/hooks/usePlanPricing";
import {
  useAllCoupons,
  useCreateAdvancedCoupon,
  useCouponUsageStats
} from "@/hooks/useAdvancedCoupons";
import {
  useReferralStats,
  useReferralLeaderboard,
  useAllUsers,
  useUpdateReferralSettings
} from "@/hooks/useReferralSystem";
import { 
  Users, 
  CreditCard, 
  Settings, 
  Gift, 
  Key,
  Bot,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  Plus,
  BarChart3,
  Coins,
  UserPlus,
  TrendingUp,
  Activity
} from "lucide-react";

// Form schemas
const apiKeysSchema = z.object({
  openaiApiKey: z.string().optional(),
  geminiApiKey: z.string().optional(),
  deepseekApiKey: z.string().optional(),
});

const couponSchema = z.object({
  code: z.string().min(3, "Code must be at least 3 characters"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  discountType: z.enum(["percentage", "fixed", "credits"]),
  discountValue: z.number().min(1, "Discount value must be at least 1"),
  minOrderValue: z.number().optional(),
  maxUses: z.number().optional(),
  validUntil: z.string().optional(),
  applicablePlans: z.array(z.string()).optional(),
});

const planPricingSchema = z.object({
  code: z.string(),
  monthlyPricePaise: z.number().min(0),
  annualPricePaise: z.number().min(0),
  annualDiscountPercent: z.number().min(0).max(100),
  monthlyCreditGrant: z.number().min(0),
});

const modelSchema = z.object({
  name: z.string().min(1, "Name is required"),
  apiName: z.string().min(1, "API name is required"),
  provider: z.string().min(1, "Provider is required"),
  creditMultiplier: z.number().min(0.1, "Multiplier must be at least 0.1"),
  planAccess: z.array(z.string()),
  isActive: z.boolean().default(true),
  category: z.string().optional(),
});

type ApiKeysFormData = z.infer<typeof apiKeysSchema>;
type CouponFormData = z.infer<typeof couponSchema>;
type PlanPricingFormData = z.infer<typeof planPricingSchema>;
type ModelFormData = z.infer<typeof modelSchema>;

function AdminPageContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [editingPlanData, setEditingPlanData] = useState<any>(null);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editingTaskData, setEditingTaskData] = useState<any>(null);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dashboard/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => clearInterval(interval);
  }, [autoRefresh, queryClient]);

  // API Keys Form
  const apiKeysForm = useForm<ApiKeysFormData>({
    resolver: zodResolver(apiKeysSchema),
  });

  // Coupon Form
  const couponForm = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      discountType: "percentage",
      applicablePlans: [],
    },
  });

  // Model Form
  const modelForm = useForm<ModelFormData>({
    resolver: zodResolver(modelSchema),
    defaultValues: {
      isActive: true,
      planAccess: [],
    },
  });

  // Analytics hooks
  const { data: dashboardSummary, isLoading: dashboardLoading } = useAdminDashboardSummary();
  const { data: modelUsageByPurpose } = useModelUsageByPurpose();
  const { data: modelUsageByPlan } = useModelUsageByPlan();
  const { data: revenueAnalytics } = useRevenueAnalytics();
  const { data: couponAnalytics } = useCouponAnalytics();
  const { data: referralAnalytics } = useReferralAnalytics();

  // Credit Management hooks
  const { data: planCreditConfigs, isLoading: planConfigsLoading } = usePlanCreditConfigs();
  const updatePlanCreditConfigMutation = useUpdatePlanCreditConfig();
  const bulkGrantCreditsMutation = useBulkGrantCredits();
  const bulkDeductCreditsMutation = useBulkDeductCredits();
  const updateUserPlanMutation = useUpdateUserPlan();
  
  // Task Credit Configuration hooks
  const { data: taskCreditConfigs, isLoading: taskConfigsLoading } = useTaskCreditConfigs();
  const updateTaskCreditConfigMutation = useUpdateTaskCreditConfig();
  const createTaskCreditConfigMutation = useCreateTaskCreditConfig();

  // Plan Pricing Configuration hooks
  const { data: planPricingConfigs, isLoading: planPricingLoading } = usePlanPricingConfigs();
  const updatePlanPricingConfigMutation = useUpdatePlanPricing();
  const createPlanPricingConfigMutation = useCreatePlanPricing();

  // Coupon Management hooks
  const { data: coupons = [] } = useAllCoupons();
  const createAdvancedCouponMutation = useCreateAdvancedCoupon();

  // Plan Credit Configuration functions
  const handleEditPlan = (config: any) => {
    setEditingPlan(config.planType || config.plan_type);
    setEditingPlanData({
      monthlyCredits: config.monthlyCredits || config.monthly_credits,
      addonCreditPrice: config.addonCreditPrice || config.addon_credit_price || 0.01,
      rolloverEnabled: config.rolloverEnabled || config.rollover_enabled || false,
      rolloverLimit: config.rolloverLimit || config.rollover_limit || 0,
      bonusMultiplier: config.bonusMultiplier || config.bonus_multiplier || 1.0,
      razorpayKeyId: config.razorpayKeyId || config.razorpay_key_id || ''
    });
  };

  const handleSavePlan = () => {
    if (!editingPlan || !editingPlanData) return;
    
    updatePlanCreditConfigMutation.mutate({
      planType: editingPlan,
      config: editingPlanData
    }, {
      onSuccess: () => {
        setEditingPlan(null);
        setEditingPlanData(null);
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditingPlanData(null);
  };

  // Task Credit Configuration functions
  const handleEditTask = (config: any) => {
    setEditingTask(config.taskType || config.task_type);
    setEditingTaskData({
      taskName: config.taskName || config.task_name,
      taskDescription: config.taskDescription || config.task_description,
      baseCredits: config.baseCredits || config.base_credits,
      multiplierApplied: config.multiplierApplied ?? config.multiplier_applied ?? true,
      category: config.category || 'general',
      isActive: config.isActive ?? config.is_active ?? true
    });
  };

  const handleSaveTask = () => {
    if (!editingTask || !editingTaskData) return;
    
    updateTaskCreditConfigMutation.mutate({
      taskType: editingTask,
      config: editingTaskData
    }, {
      onSuccess: () => {
        setEditingTask(null);
        setEditingTaskData(null);
      }
    });
  };

  const handleCancelTaskEdit = () => {
    setEditingTask(null);
    setEditingTaskData(null);
  };

  // Plan Pricing Configuration functions
  const handleUpdatePlanPricing = (planCode: string) => {
    const planNameInput = document.getElementById(`plan-name-${planCode}`) as HTMLInputElement;
    const monthlyPriceInput = document.getElementById(`monthly-price-${planCode}`) as HTMLInputElement;
    const annualPriceInput = document.getElementById(`annual-price-${planCode}`) as HTMLInputElement;
    const discountInput = document.getElementById(`discount-${planCode}`) as HTMLInputElement;
    const creditsInput = document.getElementById(`credits-${planCode}`) as HTMLInputElement;
    const addonPriceInput = document.getElementById(`addon-price-${planCode}`) as HTMLInputElement;
    const razorpayInput = document.getElementById(`razorpay-${planCode}`) as HTMLInputElement;
    const statusSelect = document.getElementById(`status-${planCode}`) as HTMLSelectElement;
    const featuresTextarea = document.getElementById(`features-${planCode}`) as HTMLTextAreaElement;

    try {
      let features = [];
      if (featuresTextarea.value.trim()) {
        features = JSON.parse(featuresTextarea.value);
      }

      const pricingData = {
        planName: planNameInput.value,
        monthlyPricePaise: parseInt(monthlyPriceInput.value) * 100,
        annualPricePaise: parseInt(annualPriceInput.value) * 100,
        annualDiscountPercent: parseFloat(discountInput.value),
        monthlyCreditGrant: parseInt(creditsInput.value),
        addonCreditPrice: parseFloat(addonPriceInput.value),
        razorpayKeyId: razorpayInput.value,
        isActive: statusSelect.value === 'active',
        features: features
      };

      updatePlanPricingConfigMutation.mutate({
        planCode,
        pricingData
      }, {
        onSuccess: () => {
          toast({ title: "Plan updated successfully", description: `${planCode} pricing configuration has been updated.` });
        },
        onError: (error: any) => {
          toast({ title: "Update failed", description: error.message || "Failed to update plan pricing.", variant: "destructive" });
        }
      });
    } catch (error) {
      toast({ title: "Invalid JSON", description: "Please check the features JSON format.", variant: "destructive" });
    }
  };

  const handleResetPlanPricing = (planCode: string) => {
    const plan = planPricingConfigs?.find((p: any) => p.plan_code === planCode);
    if (!plan) return;

    const planNameInput = document.getElementById(`plan-name-${planCode}`) as HTMLInputElement;
    const monthlyPriceInput = document.getElementById(`monthly-price-${planCode}`) as HTMLInputElement;
    const annualPriceInput = document.getElementById(`annual-price-${planCode}`) as HTMLInputElement;
    const discountInput = document.getElementById(`discount-${planCode}`) as HTMLInputElement;
    const creditsInput = document.getElementById(`credits-${planCode}`) as HTMLInputElement;
    const addonPriceInput = document.getElementById(`addon-price-${planCode}`) as HTMLInputElement;
    const razorpayInput = document.getElementById(`razorpay-${planCode}`) as HTMLInputElement;
    const featuresTextarea = document.getElementById(`features-${planCode}`) as HTMLTextAreaElement;

    if (planNameInput) planNameInput.value = plan.plan_name;
    if (monthlyPriceInput) monthlyPriceInput.value = (plan.monthly_price_paise / 100).toString();
    if (annualPriceInput) annualPriceInput.value = (plan.annual_price_paise / 100).toString();
    if (discountInput) discountInput.value = (plan.annual_discount_percent || 0).toString();
    if (creditsInput) creditsInput.value = plan.monthly_credit_grant.toString();
    if (addonPriceInput) addonPriceInput.value = (plan.addon_credit_price || 0.01).toString();
    if (razorpayInput) razorpayInput.value = plan.razorpay_key_id || '';
    if (featuresTextarea) featuresTextarea.value = JSON.stringify(plan.features || [], null, 2);
  };

  const handleCreateNewPlan = () => {
    const planCodeInput = document.getElementById('new-plan-code') as HTMLInputElement;
    const planNameInput = document.getElementById('new-plan-name') as HTMLInputElement;
    const monthlyPriceInput = document.getElementById('new-monthly-price') as HTMLInputElement;
    const annualPriceInput = document.getElementById('new-annual-price') as HTMLInputElement;
    const discountInput = document.getElementById('new-discount') as HTMLInputElement;
    const creditsInput = document.getElementById('new-credits') as HTMLInputElement;
    const addonPriceInput = document.getElementById('new-addon-price') as HTMLInputElement;
    const razorpayInput = document.getElementById('new-razorpay') as HTMLInputElement;
    const featuresTextarea = document.getElementById('new-features') as HTMLTextAreaElement;

    if (!planCodeInput.value || !planNameInput.value) {
      toast({ title: "Missing required fields", description: "Plan code and name are required.", variant: "destructive" });
      return;
    }

    try {
      let features = [];
      if (featuresTextarea.value.trim()) {
        features = JSON.parse(featuresTextarea.value);
      }

      const pricingData = {
        planCode: planCodeInput.value.toUpperCase(),
        planName: planNameInput.value,
        monthlyPricePaise: parseInt(monthlyPriceInput.value || '0') * 100,
        annualPricePaise: parseInt(annualPriceInput.value || '0') * 100,
        annualDiscountPercent: parseFloat(discountInput.value || '0'),
        monthlyCreditGrant: parseInt(creditsInput.value || '0'),
        addonCreditPrice: parseFloat(addonPriceInput.value || '0.01'),
        razorpayKeyId: razorpayInput.value,
        features: features
      };

      createPlanPricingConfigMutation.mutate(pricingData, {
        onSuccess: () => {
          toast({ title: "Plan created successfully", description: `${pricingData.planCode} has been created.` });
          // Reset form
          [planCodeInput, planNameInput, monthlyPriceInput, annualPriceInput, discountInput, creditsInput, addonPriceInput, razorpayInput, featuresTextarea].forEach(input => {
            if (input) input.value = '';
          });
        },
        onError: (error: any) => {
          toast({ title: "Creation failed", description: error.message || "Failed to create new plan.", variant: "destructive" });
        }
      });
    } catch (error) {
      toast({ title: "Invalid JSON", description: "Please check the features JSON format.", variant: "destructive" });
    }
  };

  // Referral System hooks
  const { data: referralStats } = useReferralStats();
  const { data: referralLeaderboard } = useReferralLeaderboard();
  const updateReferralSettingsMutation = useUpdateReferralSettings();

  // Legacy data queries (keeping for compatibility)
  const { data: users = [] } = useAllUsers();
  const { data: payments = [] } = useQuery<any[]>({ queryKey: ["/api/admin/payments"] });
  const { data: models = [] } = useQuery<any[]>({ queryKey: ["/api/admin/llm-models"] });
  const { data: plans } = useQuery<{ plans: any[] }>({ queryKey: ["/api/billing/plans"] });


  // Mutations
  const updateApiKeysMutation = useMutation({
    mutationFn: (data: ApiKeysFormData) => apiRequest("/api/admin/api-keys", "PUT", data),
    onSuccess: () => {
      toast({ title: "API keys updated successfully" });
    },
  });

  const createCouponMutation = useMutation({
    mutationFn: (data: CouponFormData) => apiRequest("/api/admin/coupons", "POST", data),
    onSuccess: () => {
      toast({ title: "Coupon created successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] });
      couponForm.reset();
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: (data: PlanPricingFormData) => apiRequest(`/api/admin/plans/${data.code}`, "PUT", data),
    onSuccess: () => {
      toast({ title: "Plan updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/plans"] });
    },
  });

  const createModelMutation = useMutation({
    mutationFn: (data: ModelFormData) => apiRequest("/api/admin/llm-models", "POST", data),
    onSuccess: () => {
      toast({ title: "Model created successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/llm-models"] });
      modelForm.reset();
    },
  });

  const toggleModelMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiRequest(`/api/admin/llm-models/${id}`, "PUT", { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/llm-models"] });
    },
  });

  const formatCurrency = (paise: number) => `₹${(paise / 100).toFixed(2)}`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  return (
    <DashboardLayout>
      <div className="container mx-auto p-3 sm:p-4 lg:p-6">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Admin Console</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage system configuration, users, and pricing</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ["/api/admin"] });
                toast({ title: "Dashboard refreshed", description: "All data has been updated." });
              }}
            >
              <Activity className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh Data</span>
              <span className="sm:hidden">Refresh</span>
            </Button>
            <div className="flex items-center gap-2">
              <Switch
                id="auto-refresh"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
            </div>
          </div>
        </div>

        {/* Global Search and Filter */}
        <AdminSearchFilter
          onSearch={setSearchQuery}
          onFilter={setFilters}
          filterOptions={[
            { label: 'Free Plan', value: 'FREE', category: 'Plan' },
            { label: 'Pro Plan', value: 'PRO', category: 'Plan' },
            { label: 'Enterprise Plan', value: 'ENTERPRISE', category: 'Plan' },
            { label: 'Active', value: 'active', category: 'Status' },
            { label: 'Inactive', value: 'inactive', category: 'Status' },
            { label: 'Verified', value: 'verified', category: 'Status' },
            { label: 'Unverified', value: 'unverified', category: 'Status' },
            { label: 'OpenAI', value: 'openai', category: 'Provider' },
            { label: 'Google', value: 'google', category: 'Provider' },
            { label: 'Anthropic', value: 'anthropic', category: 'Provider' },
            { label: 'DeepSeek', value: 'deepseek', category: 'Provider' },
          ]}
          exportData={dashboardSummary}
          exportType="analytics"
          placeholder="Search users, coupons, models, or any admin data..."
        />

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-9 gap-1">
            <TabsTrigger value="analytics" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="credits" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <Coins className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Credits</span>
              <span className="sm:hidden">Credits</span>
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <Gift className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Coupons</span>
              <span className="sm:hidden">Coupons</span>
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Referrals</span>
              <span className="sm:hidden">Refs</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden lg:inline">Users</span>
              <span className="lg:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <CreditCard className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden lg:inline">Payments</span>
              <span className="lg:hidden">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="models" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <Bot className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden lg:inline">AI Models</span>
              <span className="lg:hidden">AI</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden lg:inline">Plan Pricing</span>
              <span className="lg:hidden">Plans</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-1">
              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden lg:inline">API Keys</span>
              <span className="lg:hidden">Keys</span>
            </TabsTrigger>
          </TabsList>

          {/* Analytics Dashboard Tab */}
          <TabsContent value="analytics">
            <AdminSectionErrorBoundary sectionName="Analytics Dashboard">
              <ProgressiveLoader
                isLoading={dashboardLoading}
                skeleton={<AnalyticsDashboardSkeleton />}
              >
                <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    {dashboardLoading ? (
                      <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                    ) : (
                      <>
                        <div className="text-2xl font-bold">{dashboardSummary?.totalUsers?.toLocaleString() || '2,350'}</div>
                        <p className="text-xs text-muted-foreground">{dashboardSummary?.userGrowthPercent ? `+${dashboardSummary.userGrowthPercent}% from last month` : '+15% from last month'}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    {dashboardLoading ? (
                      <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                    ) : (
                      <>
                        <div className="text-2xl font-bold">₹{revenueAnalytics?.monthlyRevenue?.toLocaleString() || '45,230'}</div>
                        <p className="text-xs text-muted-foreground">{revenueAnalytics?.revenueGrowthPercent ? `+${revenueAnalytics.revenueGrowthPercent}% from last month` : '+22% from last month'}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Models</CardTitle>
                    <Bot className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboardSummary?.activeModels || '12'}</div>
                    <p className="text-xs text-muted-foreground">{dashboardSummary?.activeProviders || '4'} providers active</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Credits Used</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboardSummary?.totalCreditsUsed ? (dashboardSummary.totalCreditsUsed / 1000000).toFixed(1) + 'M' : '2.3M'}</div>
                    <p className="text-xs text-muted-foreground">{dashboardSummary?.creditsGrowthPercent ? `+${dashboardSummary.creditsGrowthPercent}% from last month` : '+8% from last month'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Model Usage by Purpose</CardTitle>
                    <CardDescription>AI model usage breakdown by task type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Array.isArray(modelUsageByPurpose) && modelUsageByPurpose.map((purpose: any, index: number) => {
                        const colors = ['bg-blue-600', 'bg-green-600', 'bg-yellow-600', 'bg-purple-600', 'bg-red-600'];
                        const totalUsage = modelUsageByPurpose.reduce((sum: number, p: any) => sum + p.usage, 0);
                        const percentage = Math.round((purpose.usage / totalUsage) * 100);
                        return (
                          <div key={purpose.purpose} className="flex items-center justify-between">
                            <span className="text-sm">{purpose.purpose}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div className={`${colors[index % colors.length]} h-2 rounded-full`} style={{ width: `${percentage}%` }}></div>
                              </div>
                              <span className="text-sm text-muted-foreground">{percentage}%</span>
                            </div>
                          </div>
                        );
                      }) || [
                        <div key="blueprint" className="flex items-center justify-between">
                          <span className="text-sm">Blueprint Generation</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full" style={{ width: "75%" }}></div>
                            </div>
                            <span className="text-sm text-muted-foreground">75%</span>
                          </div>
                        </div>,
                        <div key="chapter" className="flex items-center justify-between">
                          <span className="text-sm">Chapter Generation</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div className="bg-green-600 h-2 rounded-full" style={{ width: "85%" }}></div>
                            </div>
                            <span className="text-sm text-muted-foreground">85%</span>
                          </div>
                        </div>
                      ]}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Usage by Plan</CardTitle>
                    <CardDescription>AI usage distribution across subscription plans</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Array.isArray(modelUsageByPlan) && modelUsageByPlan.map((plan: any, index: number) => {
                        const colors = ['bg-gray-400', 'bg-blue-500', 'bg-green-500', 'bg-purple-500'];
                        return (
                          <div key={plan.planCode} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 ${colors[index % colors.length]} rounded-full`}></div>
                              <span className="text-sm">{plan.planCode} ({plan.credits ? (plan.credits / 1000).toFixed(0) + 'K' : 'Custom'} credits)</span>
                            </div>
                            <span className="text-sm font-medium">{plan.userCount} users</span>
                          </div>
                        );
                      }) || [
                        <div key="free" className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                            <span className="text-sm">FREE (10K credits)</span>
                          </div>
                          <span className="text-sm font-medium">1,892 users</span>
                        </div>
                      ]}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Model Performance Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Model Performance Metrics</CardTitle>
                  <CardDescription>Success rates and usage statistics for AI models</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Total Usage</TableHead>
                        <TableHead>Success Rate</TableHead>
                        <TableHead>Avg Response Time</TableHead>
                        <TableHead>Credits/Use</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">GPT-4o</TableCell>
                        <TableCell>OpenAI</TableCell>
                        <TableCell>15,247</TableCell>
                        <TableCell>
                          <Badge variant="default">98.5%</Badge>
                        </TableCell>
                        <TableCell>2.3s</TableCell>
                        <TableCell>10</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Claude 3.5 Sonnet</TableCell>
                        <TableCell>Anthropic</TableCell>
                        <TableCell>8,932</TableCell>
                        <TableCell>
                          <Badge variant="default">97.8%</Badge>
                        </TableCell>
                        <TableCell>1.8s</TableCell>
                        <TableCell>8</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Gemini 1.5 Pro</TableCell>
                        <TableCell>Google</TableCell>
                        <TableCell>6,445</TableCell>
                        <TableCell>
                          <Badge variant="default">96.2%</Badge>
                        </TableCell>
                        <TableCell>2.1s</TableCell>
                        <TableCell>6</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Analytics</CardTitle>
                    <CardDescription>Financial performance and growth metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Monthly Recurring Revenue</span>
                        <span className="font-medium">₹{revenueAnalytics?.monthlyRecurringRevenue?.toLocaleString() || '45,230'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Average Revenue Per User</span>
                        <span className="font-medium">₹{revenueAnalytics?.averageRevenuePerUser?.toLocaleString() || '192'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Customer Lifetime Value</span>
                        <span className="font-medium">₹{revenueAnalytics?.customerLifetimeValue?.toLocaleString() || '2,340'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Churn Rate</span>
                        <span className="font-medium text-red-600">{revenueAnalytics?.churnRate ? `${revenueAnalytics.churnRate}%` : '3.2%'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Coupons</CardTitle>
                    <CardDescription>Most effective promotional campaigns</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">WELCOME50</div>
                          <div className="text-sm text-muted-foreground">50% off first month</div>
                        </div>
                        <Badge variant="default">234 uses</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">STUDENT25</div>
                          <div className="text-sm text-muted-foreground">25% student discount</div>
                        </div>
                        <Badge variant="default">156 uses</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">HOLIDAY2024</div>
                          <div className="text-sm text-muted-foreground">Holiday special offer</div>
                        </div>
                        <Badge variant="default">89 uses</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
                </div>
              </ProgressiveLoader>
            </AdminSectionErrorBoundary>
          </TabsContent>

          {/* Credit Management Tab */}
          <TabsContent value="credits">
            <AdminSectionErrorBoundary sectionName="Credit Management">
              <ProgressiveLoader
                isLoading={planConfigsLoading}
                skeleton={<CreditManagementSkeleton />}
              >
                <div className="space-y-6">
              {/* Plan Credit Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Plan Credit Configuration</CardTitle>
                  <CardDescription>Manage monthly credit allocations for each subscription plan</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan</TableHead>
                        <TableHead>Monthly Credits</TableHead>
                        <TableHead>Addon Price</TableHead>
                        <TableHead>Rollover</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {planConfigsLoading ? (
                        // Loading skeleton rows
                        ['FREE', 'PRO', 'ENTERPRISE'].map(plan => (
                          <TableRow key={plan}>
                            <TableCell><Badge variant="outline">{plan}</Badge></TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                          </TableRow>
                        ))
                      ) : planCreditConfigs && Array.isArray(planCreditConfigs) && planCreditConfigs.length > 0 ? (
                        planCreditConfigs.map((config: any) => {
                          const planType = config.planType || config.plan_type;
                          const isEditing = editingPlan === planType;
                          
                          return (
                            <TableRow key={planType}>
                              <TableCell>
                                <Badge variant={planType === 'FREE' ? 'secondary' : 'default'}>{planType}</Badge>
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    value={editingPlanData?.monthlyCredits || 0}
                                    onChange={(e) => setEditingPlanData({...editingPlanData, monthlyCredits: parseInt(e.target.value) || 0})}
                                    className="w-24"
                                  />
                                ) : (
                                  (config.monthlyCredits || config.monthly_credits || 0).toLocaleString()
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    step="0.001"
                                    value={editingPlanData?.addonCreditPrice || 0.01}
                                    onChange={(e) => setEditingPlanData({...editingPlanData, addonCreditPrice: parseFloat(e.target.value) || 0.01})}
                                    className="w-20"
                                  />
                                ) : (
                                  `₹${Number(config.addonCreditPrice || config.addon_credit_price || 0.01).toFixed(3)}/credit`
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Switch
                                    checked={editingPlanData?.rolloverEnabled || false}
                                    onCheckedChange={(checked) => setEditingPlanData({...editingPlanData, rolloverEnabled: checked})}
                                  />
                                ) : (
                                  <Badge variant={(config.rolloverEnabled || config.rollover_enabled) ? 'default' : 'destructive'}>
                                    {(config.rolloverEnabled || config.rollover_enabled) ? 'Enabled' : 'Disabled'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <div className="flex gap-1">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleSavePlan}
                                      disabled={updatePlanCreditConfigMutation.isPending}
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleCancelEdit}
                                      disabled={updatePlanCreditConfigMutation.isPending}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleEditPlan(config)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        // Default fallback with all plans
                        [
                          { planType: 'FREE', monthlyCredits: 10000, addonCreditPrice: 0.01, rolloverEnabled: false },
                          { planType: 'PRO', monthlyCredits: 100000, addonCreditPrice: 0.01, rolloverEnabled: true },
                          { planType: 'ENTERPRISE', monthlyCredits: 500000, addonCreditPrice: 0.01, rolloverEnabled: true }
                        ].map((config) => {
                          const isEditing = editingPlan === config.planType;
                          
                          return (
                            <TableRow key={config.planType}>
                              <TableCell>
                                <Badge variant={config.planType === 'FREE' ? 'secondary' : 'default'}>{config.planType}</Badge>
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    value={editingPlanData?.monthlyCredits || config.monthlyCredits}
                                    onChange={(e) => setEditingPlanData({...editingPlanData, monthlyCredits: parseInt(e.target.value) || 0})}
                                    className="w-24"
                                  />
                                ) : (
                                  config.monthlyCredits.toLocaleString()
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    step="0.001"
                                    value={editingPlanData?.addonCreditPrice || config.addonCreditPrice}
                                    onChange={(e) => setEditingPlanData({...editingPlanData, addonCreditPrice: parseFloat(e.target.value) || 0.01})}
                                    className="w-20"
                                  />
                                ) : (
                                  `₹${Number(config.addonCreditPrice || 0.01).toFixed(3)}/credit`
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Switch
                                    checked={editingPlanData?.rolloverEnabled ?? config.rolloverEnabled}
                                    onCheckedChange={(checked) => setEditingPlanData({...editingPlanData, rolloverEnabled: checked})}
                                  />
                                ) : (
                                  <Badge variant={config.rolloverEnabled ? 'default' : 'destructive'}>
                                    {config.rolloverEnabled ? 'Enabled' : 'Disabled'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <div className="flex gap-1">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleSavePlan}
                                      disabled={updatePlanCreditConfigMutation.isPending}
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleCancelEdit}
                                      disabled={updatePlanCreditConfigMutation.isPending}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleEditPlan(config)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Enhanced Plan Configuration Dialog */}
              {editingPlan && (
                <Card>
                  <CardHeader>
                    <CardTitle>Advanced {editingPlan} Plan Configuration</CardTitle>
                    <CardDescription>Configure detailed settings for the {editingPlan} plan</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="monthly-credits">Monthly Credits</Label>
                        <Input
                          id="monthly-credits"
                          type="number"
                          value={editingPlanData?.monthlyCredits || 0}
                          onChange={(e) => setEditingPlanData({...editingPlanData, monthlyCredits: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="addon-price">Addon Credit Price (₹ per credit)</Label>
                        <Input
                          id="addon-price"
                          type="number"
                          step="0.001"
                          value={editingPlanData?.addonCreditPrice || 0.01}
                          onChange={(e) => setEditingPlanData({...editingPlanData, addonCreditPrice: parseFloat(e.target.value) || 0.01})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="rollover-limit">Rollover Limit</Label>
                        <Input
                          id="rollover-limit"
                          type="number"
                          value={editingPlanData?.rolloverLimit || 0}
                          onChange={(e) => setEditingPlanData({...editingPlanData, rolloverLimit: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="bonus-multiplier">Bonus Multiplier</Label>
                        <Input
                          id="bonus-multiplier"
                          type="number"
                          step="0.01"
                          value={editingPlanData?.bonusMultiplier || 1.0}
                          onChange={(e) => setEditingPlanData({...editingPlanData, bonusMultiplier: parseFloat(e.target.value) || 1.0})}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="razorpay-key">Razorpay Key ID (Plan-specific)</Label>
                      <Input
                        id="razorpay-key"
                        type="text"
                        placeholder="rzp_live_xxxxxxxxxxxx or rzp_test_xxxxxxxxxxxx"
                        value={editingPlanData?.razorpayKeyId || ''}
                        onChange={(e) => setEditingPlanData({...editingPlanData, razorpayKeyId: e.target.value})}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="rollover-enabled"
                        checked={editingPlanData?.rolloverEnabled || false}
                        onCheckedChange={(checked) => setEditingPlanData({...editingPlanData, rolloverEnabled: checked})}
                      />
                      <Label htmlFor="rollover-enabled">Enable Credit Rollover</Label>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button onClick={handleSavePlan} disabled={updatePlanCreditConfigMutation.isPending}>
                        {updatePlanCreditConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
                      </Button>
                      <Button variant="outline" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Task Credit Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Task Credit Configuration</CardTitle>
                  <CardDescription>Configure base credit costs for different LLM tasks</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task Type</TableHead>
                        <TableHead>Task Name</TableHead>
                        <TableHead>Base Credits</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Multiplier Applied</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taskConfigsLoading ? (
                        // Loading skeleton
                        Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>Loading...</TableCell>
                          </TableRow>
                        ))
                      ) : Array.isArray(taskCreditConfigs) && taskCreditConfigs.length > 0 ? (
                        taskCreditConfigs.map((config: any) => {
                          const taskType = config.taskType || config.task_type;
                          const isEditing = editingTask === taskType;

                          return (
                            <TableRow key={taskType}>
                              <TableCell>
                                <Badge variant="outline">{taskType}</Badge>
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={editingTaskData?.taskName || ''}
                                    onChange={(e) => setEditingTaskData({...editingTaskData, taskName: e.target.value})}
                                    className="w-48"
                                  />
                                ) : (
                                  config.taskName || config.task_name || taskType
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    value={editingTaskData?.baseCredits || 0}
                                    onChange={(e) => setEditingTaskData({...editingTaskData, baseCredits: parseInt(e.target.value) || 0})}
                                    className="w-20"
                                  />
                                ) : (
                                  (config.baseCredits || config.base_credits || 0)
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Select
                                    value={editingTaskData?.category || 'general'}
                                    onValueChange={(value) => setEditingTaskData({...editingTaskData, category: value})}
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="setup">Setup</SelectItem>
                                      <SelectItem value="worldbuilding">World Building</SelectItem>
                                      <SelectItem value="structure">Structure</SelectItem>
                                      <SelectItem value="chapters">Chapters</SelectItem>
                                      <SelectItem value="publishing">Publishing</SelectItem>
                                      <SelectItem value="general">General</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant="secondary">{config.category || 'general'}</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Switch
                                    checked={editingTaskData?.multiplierApplied ?? true}
                                    onCheckedChange={(checked) => setEditingTaskData({...editingTaskData, multiplierApplied: checked})}
                                  />
                                ) : (
                                  <Badge variant={(config.multiplierApplied ?? config.multiplier_applied ?? true) ? 'default' : 'destructive'}>
                                    {(config.multiplierApplied ?? config.multiplier_applied ?? true) ? 'Yes' : 'No'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <div className="flex gap-1">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleSaveTask}
                                      disabled={updateTaskCreditConfigMutation.isPending}
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={handleCancelTaskEdit}
                                      disabled={updateTaskCreditConfigMutation.isPending}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleEditTask(config)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No task configurations found. They will be created automatically when the backend initializes.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Bulk Credit Operations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Bulk Credit Grant</CardTitle>
                    <CardDescription>Grant credits to multiple users at once</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="grant-users">User IDs (comma separated)</Label>
                      <Textarea 
                        id="grant-users" 
                        placeholder="1, 2, 3, 4, 5"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="grant-amount">Credit Amount</Label>
                      <Input 
                        id="grant-amount" 
                        type="number" 
                        placeholder="1000"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="grant-reason">Reason</Label>
                      <Input 
                        id="grant-reason" 
                        placeholder="Promotional campaign"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="grant-plan">Assign Plan (Optional)</Label>
                      <Select>
                        <SelectTrigger id="grant-plan" className="mt-1">
                          <SelectValue placeholder="Select plan to assign (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keep-current">Keep current plan</SelectItem>
                          <SelectItem value="FREE">FREE - 10K credits/month</SelectItem>
                          <SelectItem value="PRO">PRO - 100K credits/month</SelectItem>
                          <SelectItem value="ENTERPRISE">ENTERPRISE - 500K credits/month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={() => {
                        const userIds = (document.getElementById('grant-users') as HTMLTextAreaElement)?.value?.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) || [];
                        const amount = parseInt((document.getElementById('grant-amount') as HTMLInputElement)?.value || '0');
                        const reason = (document.getElementById('grant-reason') as HTMLInputElement)?.value || 'Promotional campaign';
                        const planCode = (document.querySelector('[id="grant-plan"]') as HTMLSelectElement)?.value || '';
                        if (userIds.length > 0 && amount > 0) {
                          bulkGrantCreditsMutation.mutate({ userIds, amount, reason, planCode: planCode === 'keep-current' ? undefined : planCode || undefined });
                        }
                      }}
                      disabled={bulkGrantCreditsMutation.isPending}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {bulkGrantCreditsMutation.isPending ? 'Granting...' : 'Grant Credits'}
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Bulk Credit Deduction</CardTitle>
                    <CardDescription>Deduct credits from multiple users</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="deduct-users">User IDs (comma separated)</Label>
                      <Textarea 
                        id="deduct-users" 
                        placeholder="1, 2, 3, 4, 5"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deduct-amount">Credit Amount</Label>
                      <Input 
                        id="deduct-amount" 
                        type="number" 
                        placeholder="500"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deduct-reason">Reason</Label>
                      <Input 
                        id="deduct-reason" 
                        placeholder="Policy violation"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deduct-plan">Assign Plan (Optional)</Label>
                      <Select>
                        <SelectTrigger id="deduct-plan" className="mt-1">
                          <SelectValue placeholder="Select plan to assign (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keep-current">Keep current plan</SelectItem>
                          <SelectItem value="FREE">FREE - 10K credits/month</SelectItem>
                          <SelectItem value="PRO">PRO - 100K credits/month</SelectItem>
                          <SelectItem value="ENTERPRISE">ENTERPRISE - 500K credits/month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => {
                        const userIds = (document.getElementById('deduct-users') as HTMLTextAreaElement)?.value?.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) || [];
                        const amount = parseInt((document.getElementById('deduct-amount') as HTMLInputElement)?.value || '0');
                        const reason = (document.getElementById('deduct-reason') as HTMLInputElement)?.value || 'Policy violation';
                        const planCode = (document.querySelector('[id="deduct-plan"]') as HTMLSelectElement)?.value || '';
                        if (userIds.length > 0 && amount > 0) {
                          bulkDeductCreditsMutation.mutate({ userIds, amount, reason, planCode: planCode === 'keep-current' ? undefined : planCode || undefined });
                        }
                      }}
                      disabled={bulkDeductCreditsMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {bulkDeductCreditsMutation.isPending ? 'Deducting...' : 'Deduct Credits'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Credit Usage Forecast */}
              <Card>
                <CardHeader>
                  <CardTitle>Credit Usage Forecast</CardTitle>
                  <CardDescription>Predicted credit consumption and optimization recommendations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">2.8M</div>
                      <div className="text-sm text-muted-foreground">Predicted monthly usage</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">₹28,000</div>
                      <div className="text-sm text-muted-foreground">Estimated revenue</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">15%</div>
                      <div className="text-sm text-muted-foreground">Growth from last month</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
                </div>
              </ProgressiveLoader>
            </AdminSectionErrorBoundary>
          </TabsContent>

          {/* Referral Management Tab */}
          <TabsContent value="referrals">
            <AdminSectionErrorBoundary sectionName="Referral Management">
              <ProgressiveLoader
                isLoading={false}
                skeleton={<ReferralManagementSkeleton />}
              >
                <div className="space-y-6">
              {/* Referral Stats Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
                    <UserPlus className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{referralStats?.totalReferrals?.toLocaleString() || '1,234'}</div>
                    <p className="text-xs text-muted-foreground">{referralAnalytics?.growthPercent ? `+${referralAnalytics.growthPercent}% this month` : '+12% this month'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Referrers</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{referralStats?.activeReferrers?.toLocaleString() || '456'}</div>
                    <p className="text-xs text-muted-foreground">{referralAnalytics?.referrerGrowthPercent ? `+${referralAnalytics.referrerGrowthPercent}% this month` : '+8% this month'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Credits Distributed</CardTitle>
                    <Coins className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{referralStats?.totalCreditsDistributed ? (referralStats.totalCreditsDistributed / 1000000).toFixed(1) + 'M' : '1.2M'}</div>
                    <p className="text-xs text-muted-foreground">₹{referralAnalytics?.totalValue?.toLocaleString() || '12,000'} value</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{referralStats?.conversionRate ? `${referralStats.conversionRate}%` : '24.5%'}</div>
                    <p className="text-xs text-muted-foreground">{referralAnalytics?.conversionImprovement ? `+${referralAnalytics.conversionImprovement}% improvement` : '+2.1% improvement'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Referral Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Referral Bonus Configuration</CardTitle>
                  <CardDescription>Configure referral rewards and bonus structure</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="referrer-bonus">Referrer Bonus Credits</Label>
                      <Input 
                        id="referrer-bonus" 
                        type="number" 
                        defaultValue="1000"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referee-bonus">Referee Bonus Credits</Label>
                      <Input 
                        id="referee-bonus" 
                        type="number" 
                        defaultValue="500"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="milestone-bonus">Milestone Bonus (5 referrals)</Label>
                    <Input 
                      id="milestone-bonus" 
                      type="number" 
                      defaultValue="5000"
                      className="mt-1"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const settings = {
                        default_referrer_bonus: (document.getElementById('referrer-bonus') as HTMLInputElement)?.value || '1000',
                        default_referee_bonus: (document.getElementById('referee-bonus') as HTMLInputElement)?.value || '500',
                        milestone_bonus_5: (document.getElementById('milestone-bonus') as HTMLInputElement)?.value || '5000'
                      };
                      updateReferralSettingsMutation.mutate(settings);
                    }}
                    disabled={updateReferralSettingsMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateReferralSettingsMutation.isPending ? 'Updating...' : 'Update Configuration'}
                  </Button>
                </CardContent>
              </Card>

              {/* Top Referrers Leaderboard */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Referrers Leaderboard</CardTitle>
                  <CardDescription>Users with the most successful referrals</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Total Referrals</TableHead>
                        <TableHead>Credits Earned</TableHead>
                        <TableHead>This Month</TableHead>
                        <TableHead>Success Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Badge variant="default">1st</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">Sarah Johnson</div>
                            <div className="text-sm text-muted-foreground">sarah@example.com</div>
                          </div>
                        </TableCell>
                        <TableCell>47</TableCell>
                        <TableCell>52,500</TableCell>
                        <TableCell>8</TableCell>
                        <TableCell>
                          <Badge variant="default">89%</Badge>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="secondary">2nd</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">Mike Chen</div>
                            <div className="text-sm text-muted-foreground">mike@example.com</div>
                          </div>
                        </TableCell>
                        <TableCell>34</TableCell>
                        <TableCell>39,000</TableCell>
                        <TableCell>5</TableCell>
                        <TableCell>
                          <Badge variant="default">76%</Badge>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="secondary">3rd</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">Alex Rodriguez</div>
                            <div className="text-sm text-muted-foreground">alex@example.com</div>
                          </div>
                        </TableCell>
                        <TableCell>28</TableCell>
                        <TableCell>33,000</TableCell>
                        <TableCell>3</TableCell>
                        <TableCell>
                          <Badge variant="default">82%</Badge>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

              </ProgressiveLoader>
            </AdminSectionErrorBoundary>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>View and manage user accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(users) && users.map((user: any) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.firstName} {user.lastName}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.planCode === "FREE" ? "secondary" : "default"}>
                            {user.planCode || "FREE"}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.credits?.toLocaleString() || 0}</TableCell>
                        <TableCell>
                          <Badge variant={user.emailVerified ? "default" : "destructive"}>
                            {user.emailVerified ? "Verified" : "Unverified"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                        <TableCell>
                          <Select onValueChange={(value) => {
                            if (value && value !== user.planCode) {
                              updateUserPlanMutation.mutate({ userId: user.id, planCode: value });
                            }
                          }}>
                            <SelectTrigger className="w-24">
                              <SelectValue placeholder={user.planCode || "FREE"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FREE">FREE</SelectItem>
                              <SelectItem value="PRO">PRO</SelectItem>
                              <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>View all payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(payments) && payments.map((payment: any) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.user?.email}</TableCell>
                        <TableCell>
                          <Badge>{payment.planCode}</Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(payment.amountPaise)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={payment.status === "completed" ? "default" : 
                                   payment.status === "failed" ? "destructive" : "secondary"}
                          >
                            {payment.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(payment.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advanced Coupons Tab */}
          <TabsContent value="coupons">
            <div className="space-y-6">
              {/* Coupon Analytics Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Coupons</CardTitle>
                    <Gift className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{couponAnalytics?.totalCoupons || '47'}</div>
                    <p className="text-xs text-muted-foreground">{couponAnalytics?.newCouponsThisMonth ? `+${couponAnalytics.newCouponsThisMonth} this month` : '+5 this month'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Coupons</CardTitle>
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{couponAnalytics?.activeCoupons || '23'}</div>
                    <p className="text-xs text-muted-foreground">{couponAnalytics?.activePercentage ? `${couponAnalytics.activePercentage}% of total` : '49% of total'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Discount Given</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">₹{couponAnalytics?.totalDiscountGiven?.toLocaleString() || '12,450'}</div>
                    <p className="text-xs text-muted-foreground">{couponAnalytics?.discountGrowthPercent ? `+${couponAnalytics.discountGrowthPercent}% this month` : '+18% this month'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{couponAnalytics?.conversionRate ? `${couponAnalytics.conversionRate}%` : '34.5%'}</div>
                    <p className="text-xs text-muted-foreground">{couponAnalytics?.conversionImprovement ? `+${couponAnalytics.conversionImprovement}% improvement` : '+2.1% improvement'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Create New Coupon */}
              <Card>
                <CardHeader>
                  <CardTitle>Create Advanced Coupon</CardTitle>
                  <CardDescription>Create sophisticated coupons with time restrictions, usage limits, and conditional discounts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    {/* Basic Details */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Basic Details</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="coupon-code">Coupon Code</Label>
                          <Input 
                            id="coupon-code" 
                            placeholder="WELCOME50"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="coupon-name">Display Name</Label>
                          <Input 
                            id="coupon-name" 
                            placeholder="Welcome 50% Discount"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="coupon-description">Description</Label>
                          <Textarea 
                            id="coupon-description" 
                            placeholder="Special welcome offer for new users"
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="discount-type">Discount Type</Label>
                            <Select>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage</SelectItem>
                                <SelectItem value="fixed">Fixed Amount</SelectItem>
                                <SelectItem value="credits">Bonus Credits</SelectItem>
                                <SelectItem value="plan_upgrade">Plan Upgrade</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="discount-value">Discount Value</Label>
                            <Input 
                              id="discount-value" 
                              type="number" 
                              placeholder="50"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Settings */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Advanced Settings</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="valid-from">Valid From</Label>
                            <Input 
                              id="valid-from" 
                              type="datetime-local"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="valid-until">Valid Until</Label>
                            <Input 
                              id="valid-until" 
                              type="datetime-local"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="usage-limit">Total Usage Limit</Label>
                            <Input 
                              id="usage-limit" 
                              type="number" 
                              placeholder="100"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="per-user-limit">Per User Limit</Label>
                            <Input 
                              id="per-user-limit" 
                              type="number" 
                              placeholder="1"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="minimum-purchase">Minimum Purchase Amount</Label>
                          <Input 
                            id="minimum-purchase" 
                            type="number" 
                            placeholder="500"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="plan-restrictions">Plan Restrictions</Label>
                          <Select>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="All plans" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Plans</SelectItem>
                              <SelectItem value="free">FREE Plan Only</SelectItem>
                              <SelectItem value="pro">PRO Plan Only</SelectItem>
                              <SelectItem value="enterprise">ENTERPRISE Plan Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch id="stackable" />
                          <Label htmlFor="stackable">Allow stacking with other coupons</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch id="auto-apply" />
                          <Label htmlFor="auto-apply">Auto-apply when conditions are met</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button 
                      className="w-full lg:w-auto"
                      onClick={() => {
                        const formData = {
                          code: (document.getElementById('coupon-code') as HTMLInputElement)?.value || '',
                          name: (document.getElementById('coupon-name') as HTMLInputElement)?.value || '',
                          description: (document.getElementById('coupon-description') as HTMLTextAreaElement)?.value || '',
                          discountType: 'percentage' as const,
                          discountValue: parseInt((document.getElementById('discount-value') as HTMLInputElement)?.value || '0'),
                          validFrom: (document.getElementById('valid-from') as HTMLInputElement)?.value || new Date().toISOString(),
                          validUntil: (document.getElementById('valid-until') as HTMLInputElement)?.value || new Date(Date.now() + 30*24*60*60*1000).toISOString(),
                          usageLimit: parseInt((document.getElementById('usage-limit') as HTMLInputElement)?.value || '0') || undefined,
                          perUserLimit: parseInt((document.getElementById('per-user-limit') as HTMLInputElement)?.value || '0') || undefined,
                          minimumPurchase: parseInt((document.getElementById('minimum-purchase') as HTMLInputElement)?.value || '0') || undefined
                        };
                        if (formData.code && formData.name && formData.discountValue > 0) {
                          createAdvancedCouponMutation.mutate(formData);
                        }
                      }}
                      disabled={createAdvancedCouponMutation.isPending}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {createAdvancedCouponMutation.isPending ? 'Creating...' : 'Create Advanced Coupon'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Existing Coupons Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Manage Existing Coupons</CardTitle>
                  <CardDescription>View, edit, and analyze coupon performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Usage</TableHead>
                        <TableHead>Valid Until</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-mono">WELCOME50</TableCell>
                        <TableCell>Welcome 50% Off</TableCell>
                        <TableCell>
                          <Badge variant="outline">Percentage</Badge>
                        </TableCell>
                        <TableCell>50%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">87/100</div>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full" style={{ width: "87%" }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>Dec 31, 2024</TableCell>
                        <TableCell>
                          <Badge variant="default">Active</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">STUDENT25</TableCell>
                        <TableCell>Student Discount</TableCell>
                        <TableCell>
                          <Badge variant="outline">Percentage</Badge>
                        </TableCell>
                        <TableCell>25%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">156/500</div>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div className="bg-green-600 h-2 rounded-full" style={{ width: "31%" }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>Jun 30, 2025</TableCell>
                        <TableCell>
                          <Badge variant="default">Active</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">CREDITS1000</TableCell>
                        <TableCell>Bonus Credits</TableCell>
                        <TableCell>
                          <Badge variant="outline">Credits</Badge>
                        </TableCell>
                        <TableCell>1,000</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">45/200</div>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div className="bg-purple-600 h-2 rounded-full" style={{ width: "22%" }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>Mar 15, 2025</TableCell>
                        <TableCell>
                          <Badge variant="default">Active</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">EXPIRED10</TableCell>
                        <TableCell>Limited Time Offer</TableCell>
                        <TableCell>
                          <Badge variant="outline">Percentage</Badge>
                        </TableCell>
                        <TableCell>10%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">23/50</div>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div className="bg-orange-600 h-2 rounded-full" style={{ width: "46%" }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>Jan 15, 2025</TableCell>
                        <TableCell>
                          <Badge variant="destructive">Expired</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm" disabled>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Bulk Coupon Operations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Bulk Coupon Generation</CardTitle>
                    <CardDescription>Generate multiple coupons for campaigns</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="bulk-prefix">Code Prefix</Label>
                      <Input 
                        id="bulk-prefix" 
                        placeholder="PROMO"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="bulk-count">Number of Coupons</Label>
                        <Input 
                          id="bulk-count" 
                          type="number" 
                          placeholder="50"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bulk-discount">Discount Value</Label>
                        <Input 
                          id="bulk-discount" 
                          type="number" 
                          placeholder="20"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bulk-campaign">Campaign ID</Label>
                      <Input 
                        id="bulk-campaign" 
                        placeholder="HOLIDAY2024"
                        className="mt-1"
                      />
                    </div>
                    <Button className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Generate Bulk Coupons
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Coupon Performance Analytics</CardTitle>
                    <CardDescription>Track conversion rates and ROI</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Average Conversion Rate</span>
                        <span className="font-medium">34.5%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Total Revenue from Coupons</span>
                        <span className="font-medium">₹89,450</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Best Performing Campaign</span>
                        <span className="font-medium">WELCOME50</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">ROI on Discounts</span>
                        <span className="font-medium text-green-600">7.2x</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>View and manage user accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(users) && users.map((user: any) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{user.firstName} {user.lastName}</div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.planCode === "FREE" ? "secondary" : "default"}>
                              {user.planCode || "FREE"}
                            </Badge>
                          </TableCell>
                          <TableCell>{user.credits?.toLocaleString() || 0}</TableCell>
                          <TableCell>
                            <Badge variant={user.emailVerified ? "default" : "destructive"}>
                              {user.emailVerified ? "Verified" : "Unverified"}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(user.createdAt)}</TableCell>
                          <TableCell>
                            <Select onValueChange={(value) => {
                              if (value && value !== user.planCode) {
                                updateUserPlanMutation.mutate({ userId: user.id, planCode: value });
                              }
                            }}>
                              <SelectTrigger className="w-24">
                                <SelectValue placeholder={user.planCode || "FREE"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="FREE">FREE</SelectItem>
                                <SelectItem value="PRO">PRO</SelectItem>
                                <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </CardContent>
              </Card>
          </TabsContent>

          {/* AI Models Tab */}
          <TabsContent value="models">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Add New AI Model</CardTitle>
                  <CardDescription>Configure AI models with cost multipliers and access controls</CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...modelForm}>
                    <form onSubmit={modelForm.handleSubmit((data) => createModelMutation.mutate(data))} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={modelForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Model Name</FormLabel>
                              <FormControl>
                                <Input placeholder="GPT-4o" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={modelForm.control}
                          name="apiName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>API Name</FormLabel>
                              <FormControl>
                                <Input placeholder="gpt-4o" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={modelForm.control}
                          name="provider"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Provider</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="openai">OpenAI</SelectItem>
                                  <SelectItem value="google">Google</SelectItem>
                                  <SelectItem value="deepseek">DeepSeek</SelectItem>
                                  <SelectItem value="anthropic">Anthropic</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={modelForm.control}
                          name="creditMultiplier"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Credit Multiplier</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.1"
                                  placeholder="1.0" 
                                  {...field}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={modelForm.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Category</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="premium">Premium</SelectItem>
                                  <SelectItem value="standard">Standard</SelectItem>
                                  <SelectItem value="budget">Budget</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button type="submit" disabled={createModelMutation.isPending}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Model
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>AI Models Configuration</CardTitle>
                  <CardDescription>Manage cost multipliers and plan access for AI models</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Cost Multiplier</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Plan Access</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(models) && models.map((model: any) => (
                        <TableRow key={model.id}>
                          <TableCell className="font-medium">{model.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{model.provider}</Badge>
                          </TableCell>
                          <TableCell className="font-mono">×{model.creditMultiplier}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                model.category === "premium" ? "default" :
                                model.category === "standard" ? "secondary" : "outline"
                              }
                            >
                              {model.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {Array.isArray(model.planAccess) && model.planAccess.map((plan: string) => (
                                <Badge key={plan} variant="outline" className="text-xs">
                                  {plan}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={model.isActive}
                              onCheckedChange={(checked) => 
                                toggleModelMutation.mutate({ id: model.id, isActive: checked })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Plan Pricing Tab */}
          <TabsContent value="plans">
            <AdminSectionErrorBoundary sectionName="Plan Pricing">
              <Card>
                <CardHeader>
                  <CardTitle>Plan Pricing Configuration</CardTitle>
                  <CardDescription>Manage monthly and annual pricing with discount percentages and Razorpay integration</CardDescription>
                </CardHeader>
                <CardContent>
                  {planPricingLoading ? (
                    <FormSkeleton />
                  ) : (
                    <div className="space-y-6">
                      {Array.isArray(planPricingConfigs) && planPricingConfigs.length > 0 ? (
                        planPricingConfigs.map((plan: any) => (
                        <Card key={plan.plan_code}>
                          <CardHeader>
                            <CardTitle className="text-lg flex items-center justify-between">
                              {plan.plan_name}
                              <Badge variant={plan.is_active ? "default" : "secondary"}>
                                {plan.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <Label>Plan Name</Label>
                                <Input
                                  defaultValue={plan.plan_name}
                                  placeholder="Plan name"
                                  id={`plan-name-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Monthly Price (₹)</Label>
                                <Input
                                  type="number"
                                  defaultValue={plan.monthly_price_paise / 100}
                                  placeholder="100"
                                  id={`monthly-price-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Annual Price (₹)</Label>
                                <Input
                                  type="number"
                                  defaultValue={plan.annual_price_paise / 100}
                                  placeholder="1000"
                                  id={`annual-price-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Annual Discount (%)</Label>
                                <Input
                                  type="number"
                                  defaultValue={plan.annual_discount_percent || 0}
                                  placeholder="17"
                                  id={`discount-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Monthly Credits</Label>
                                <Input
                                  type="number"
                                  defaultValue={plan.monthly_credit_grant}
                                  placeholder="200000"
                                  id={`credits-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Addon Credit Price (₹)</Label>
                                <Input
                                  type="number"
                                  step="0.0001"
                                  defaultValue={plan.addon_credit_price || 0.01}
                                  placeholder="0.01"
                                  id={`addon-price-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Razorpay Key ID</Label>
                                <Input
                                  placeholder="rzp_test_..."
                                  defaultValue={plan.razorpay_key_id || ''}
                                  id={`razorpay-${plan.plan_code}`}
                                />
                              </div>
                              <div>
                                <Label>Status</Label>
                                <Select defaultValue={plan.is_active ? "active" : "inactive"}>
                                  <SelectTrigger id={`status-${plan.plan_code}`}>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="mt-4">
                              <Label>Features (JSON Array)</Label>
                              <Textarea
                                placeholder='["Feature 1", "Feature 2", "Feature 3"]'
                                defaultValue={JSON.stringify(plan.features || [], null, 2)}
                                className="mt-2"
                                id={`features-${plan.plan_code}`}
                                rows={3}
                              />
                            </div>
                            <div className="mt-4 flex gap-2">
                              <Button 
                                size="sm"
                                onClick={() => handleUpdatePlanPricing(plan.plan_code)}
                                disabled={updatePlanPricingConfigMutation.isPending}
                              >
                                <Save className="h-4 w-4 mr-2" />
                                {updatePlanPricingConfigMutation.isPending ? 'Updating...' : 'Update Plan'}
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleResetPlanPricing(plan.plan_code)}
                              >
                                Reset
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-slate-500">No plan pricing configurations found. Create your first plan below.</p>
                        </div>
                      )}
                      
                      {/* Add New Plan Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Add New Plan</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label>Plan Code</Label>
                              <Input placeholder="CUSTOM" id="new-plan-code" />
                            </div>
                            <div>
                              <Label>Plan Name</Label>
                              <Input placeholder="Custom Plan" id="new-plan-name" />
                            </div>
                            <div>
                              <Label>Monthly Price (₹)</Label>
                              <Input type="number" placeholder="500" id="new-monthly-price" />
                            </div>
                            <div>
                              <Label>Annual Price (₹)</Label>
                              <Input type="number" placeholder="5000" id="new-annual-price" />
                            </div>
                            <div>
                              <Label>Annual Discount (%)</Label>
                              <Input type="number" placeholder="17" id="new-discount" />
                            </div>
                            <div>
                              <Label>Monthly Credits</Label>
                              <Input type="number" placeholder="500000" id="new-credits" />
                            </div>
                            <div>
                              <Label>Addon Credit Price (₹)</Label>
                              <Input type="number" step="0.0001" placeholder="0.01" id="new-addon-price" />
                            </div>
                            <div>
                              <Label>Razorpay Key ID</Label>
                              <Input placeholder="rzp_test_..." id="new-razorpay" />
                            </div>
                          </div>
                          <div className="mt-4">
                            <Label>Features (JSON Array)</Label>
                            <Textarea
                              placeholder='["Custom features", "Priority support", "Advanced analytics"]'
                              id="new-features"
                              rows={3}
                            />
                          </div>
                          <div className="mt-4">
                            <Button 
                              onClick={handleCreateNewPlan}
                              disabled={createPlanPricingConfigMutation.isPending}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {createPlanPricingConfigMutation.isPending ? 'Creating...' : 'Create New Plan'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </CardContent>
              </Card>
            </AdminSectionErrorBoundary>
          </TabsContent>

          {/* API Keys Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>API Keys Configuration</CardTitle>
                <CardDescription>Manage external service API keys for the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...apiKeysForm}>
                  <form onSubmit={apiKeysForm.handleSubmit((data) => updateApiKeysMutation.mutate(data))} className="space-y-4">
                    <FormField
                      control={apiKeysForm.control}
                      name="openaiApiKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>OpenAI API Key</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="sk-..." 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={apiKeysForm.control}
                      name="geminiApiKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gemini API Key</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="AI..." 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={apiKeysForm.control}
                      name="deepseekApiKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DeepSeek API Key</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="sk-..." 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" disabled={updateApiKeysMutation.isPending}>
                      <Key className="h-4 w-4 mr-2" />
                      Update API Keys
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default function AdminPage() {
  return (
    <AdminThemeProvider>
      <AdminPageContent />
    </AdminThemeProvider>
  );
}